// Renderer kết quả structured theo reading_kind (v72):
// - compare: bảng so sánh 2 phương án + card đề xuất (pick / lý do / điều kiện đảo chiều)
// - timeline: dòng thời gian các mốc vận (bar theo mức độ) + best_window
import { CalendarRange, GitCompareArrows, Lightbulb } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { BoiDeCompare, BoiDeTimeline } from "@/lib/boide-api";

const MUC_LABEL: Record<string, string> = { cao: "Cao", vua: "Vừa", thap: "Thấp" };

const MUC_BADGE: Record<string, string> = {
  cao: "bg-emerald-500/15 text-emerald-300",
  vua: "bg-[#E5B869]/15 text-[#E5B869]",
  thap: "bg-red-500/15 text-red-300",
};

const MUC_BAR: Record<string, { width: string; color: string }> = {
  cao: { width: "100%", color: "bg-emerald-400/70" },
  vua: { width: "62%", color: "bg-[#E5B869]/70" },
  thap: { width: "30%", color: "bg-red-400/50" },
};

export function CompareResultCard({ compare }: { compare: BoiDeCompare }) {
  const [a, b] = compare.options;
  if (!a || !b) return null;
  const rec = compare.recommendation;
  const pickedLabel =
    rec.pick === "A" ? a.label : rec.pick === "B" ? b.label : "Tùy điều kiện";
  return (
    <div className="space-y-3">
      <Card className="border-gold-soft/40 bg-card/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <GitCompareArrows className="h-4 w-4 text-primary" /> Bảng so sánh hai lựa chọn
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto pt-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border/40 hover:bg-transparent">
                <TableHead className="w-28 text-xs" />
                {[a, b].map((o) => (
                  <TableHead key={o.key} className="text-xs font-semibold text-foreground">
                    {o.key}. {o.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody className="text-xs">
              <TableRow className="border-border/30">
                <TableCell className="text-muted-foreground">Mức thuận lợi</TableCell>
                {[a, b].map((o) => (
                  <TableCell key={o.key}>
                    <span className="flex items-center gap-2">
                      <Badge className={`${MUC_BADGE[o.muc_thuan_loi]} text-[10px]`}>
                        {MUC_LABEL[o.muc_thuan_loi]}
                      </Badge>
                      <span className="font-bold text-foreground">{o.score}/100</span>
                    </span>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="border-border/30 align-top">
                <TableCell className="text-muted-foreground">Điểm mạnh</TableCell>
                {[a, b].map((o) => (
                  <TableCell key={o.key}>
                    <ul className="list-disc space-y-0.5 pl-4">
                      {o.diem_manh.map((x, i) => (
                        <li key={i}>{x}</li>
                      ))}
                    </ul>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="border-border/30 align-top">
                <TableCell className="text-muted-foreground">Rủi ro chính</TableCell>
                {[a, b].map((o) => (
                  <TableCell key={o.key}>
                    <ul className="list-disc space-y-0.5 pl-4">
                      {o.rui_ro_chinh.map((x, i) => (
                        <li key={i}>{x}</li>
                      ))}
                    </ul>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow className="border-border/30">
                <TableCell className="text-muted-foreground">Thời điểm phù hợp</TableCell>
                {[a, b].map((o) => (
                  <TableCell key={o.key}>{o.thoi_diem_phu_hop || "—"}</TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-[#E5B869]/40 bg-[#E5B869]/5">
        <CardContent className="space-y-1.5 py-4 text-sm">
          <div className="flex items-center gap-2 font-semibold text-foreground">
            <Lightbulb className="h-4 w-4 text-[#E5B869]" />
            Thầy nghiêng về: <span className="text-gold-gradient">{pickedLabel}</span>
          </div>
          {rec.ly_do ? <p className="text-xs text-muted-foreground">{rec.ly_do}</p> : null}
          {rec.dieu_kien_thay_doi ? (
            <p className="text-xs text-muted-foreground">
              <span className="text-foreground">Kết luận đảo chiều nếu:</span>{" "}
              {rec.dieu_kien_thay_doi}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

export function TimelineResultCard({ timeline }: { timeline: BoiDeTimeline }) {
  if (!timeline.timeline.length) return null;
  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarRange className="h-4 w-4 text-primary" /> Dòng thời gian vận trình
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {timeline.timeline.map((item, i) => {
          const bar = MUC_BAR[item.muc_do] ?? MUC_BAR.vua;
          const isBest = timeline.best_window && item.period === timeline.best_window;
          return (
            <div
              key={i}
              className={`rounded-xl border p-3 ${
                isBest ? "border-[#E5B869]/60 bg-[#E5B869]/8" : "border-border/40 bg-background/30"
              }`}
            >
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{item.period}</span>
                <Badge className={`${MUC_BADGE[item.muc_do]} text-[10px]`}>
                  {MUC_LABEL[item.muc_do]}
                </Badge>
                {isBest ? (
                  <Badge className="bg-gold-gradient/20 text-[10px] text-foreground">
                    ✦ Đáng chú ý nhất
                  </Badge>
                ) : null}
              </div>
              <div className="mb-1.5 h-1.5 overflow-hidden rounded-full bg-border/40">
                <div
                  className={`h-full rounded-full ${bar.color}`}
                  style={{ width: bar.width }}
                />
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">{item.note}</p>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
